// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Editor allowing only for updating the type of a given deck edge.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate } from "../../../utils/index.js"

// @@imports-module
import { Editor } from "./Editor.js"

// @@imports-types
import { State } from "../../../types/index.js"
import { SteppedContainer } from "../../containers/SteppedContainer.js"
import { Edge } from "../../../../../package/index.js"
// import { stepUpdated } from "../../state/events.js"

// @@body
class EdgeTypeEditor extends Editor {
    #typeListener = (/** @type {Event} */ event) => {
        const value = /** @type {ListSelect<EdgeType>} */ (event.target).value
        this.deck.setEdgeType(this.edge, value)
    }

    constructor() {
        super()
    }

    connectedCallback() {
        super.connectedCallback()

        const select = cloneTemplate("edge-type-editor-select")
        const nav = cloneTemplate("edge-type-editor-nav")
        this.append(select, nav)

        this.renderer.renderFeatures.types = true
        this.renderer.render({ reset: true })

        const modalContainer = SteppedContainer.modalContainer()
        const modal = cloneTemplate("edge-type-editor-modal")
        modal.querySelector("button.primary")?.addEventListener("click", () => {
            this.container.unloadModal()
        })
        modal.querySelector("button.secondary")?.addEventListener("click", () => {
            this.container.unloadModal()
            this.remove()
            // this.container.unloadPopover().then(() => this.remove())
        })
        modalContainer.append(modal)
        this.container.loadModal(modalContainer)

        if (this.deck.isSetEdgeType(this.edge)) {
            const edgeType = this.deck.getEdgeType(this.edge)
            this.#typeSelector.value = edgeType
        }
        this.#addTypeListener()

        const nextEdgeButton = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.next-edge"))
        nextEdgeButton.addEventListener("click", () => {
            if (this.deck.isDefined.types) {
                this.renderer.selectedEdge = null
                this.remove()

                document.querySelector("stepped-container > nav button.primary")
                    ?.classList
                    .remove("inactive")

                const modalContainer = SteppedContainer.modalContainer()
                const modal = cloneTemplate("advanced-mode-selector-modal")
                modal.querySelector("button.primary")
                    ?.addEventListener("click", () => {
                        this.container.unloadModal().then(() => {
                            this.container.incrementStep()
                        })
                    })
                modal.querySelector("button.secondary")
                    ?.addEventListener("click", () => {
                        this.container.unloadModal()

                        // Trigger re-render if client continues editing such
                        // that all elements (such as edge length text) become
                        // correctly interactive.
                        this.renderer.render({ reset: true })
                    })
                modalContainer.append(modal)
                this.container.loadModal(modalContainer)
            }
            else {
                const edgeNode = this.deck.edges.nodeWithValue(this.edge)
                this.edge = /** @type {any} */ (edgeNode?.next?.value)
            }
        })

        // implement previous button such that clicking previous on first edge
        // goes back to edge length options
        // const previousEdgeButton = this.querySelector("button.previous-edge")
        // previousEdgeButton.addEventListener("click", () => {
        //     const edgeNode = /** @type {DoubleLinkedListNode<Edge>} */ (this
        //         .state
        //         .deckEditor
        //         .deck
        //         ?.edges
        //         .nodeWithValue(this.#edge))

        //     // const resizeListener = () => {
        //     //     this.removeEventListener("tween-end", resizeListener)
        //     //     this.edge = edgeNode.previous?.value
        //     // }
    
        //     // if (this.#tweening) {
        //     //     this.addEventListener("tween-end", resizeListener)
        //     //     this.#edgeLengthInput.blur()
        //     // }
        //     // else { this.edge = edgeNode.previous?.value }

        //     this.edge = edgeNode.previous?.value
        // });
    }

    #addTypeListener() {
        this.#typeSelector
            .addEventListener("change", this.#typeListener)
    }

    #removeTypeListener() {
        this.#typeSelector
            .removeEventListener("change", this.#typeListener)
    }

    get #typeSelector() {
        return /** @type {ListSelect} */ (this.querySelector("list-select"))
    }

    /**
     * @param {Edge} edge
     */
    set edge(edge) {
        this.renderer.selectedEdge = edge

        this.#removeTypeListener()
        if (this.deck.isSetEdgeType(edge)) {
            this.#typeSelector.value = this.deck.getEdgeType(edge)
        }
        else {
            this.#typeSelector.value = ""
        }
        this.#addTypeListener()
    }

    get edge() {
        return /** @type {Edge} */ (this.renderer.selectedEdge)
    }

    static get customName() {
        return "edge-type-editor"
    }
}

// @@exports
export { EdgeTypeEditor }
